import React, {ReactElement} from 'react';
import styled from 'styled-components';

import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../libs/shared/ui/src/styles/mixins';
import {AccountModel} from '../../../../models/api/accountModel';
import {AccountAvatar} from '../../avatars/accountAvatar';
import {Skeleton, SkeletonColorSchemesEnum} from '../../skeleton';
import {
  getCellBackgroundColor,
  getCellIndexStyles,
  getCellTextColor,
  getTableCellAvatarIconSize,
  getTableCellAvatarSize,
} from '../helpers/tableCellHelpers';
import {getWidthSettingsStyles} from '../tableColumn';
import {TableCell, TableCellPropsWithAvatar, TableCellStyleProps} from './tableCell';

/*
 * Styles.
 */

const StyledAccountCellContainerDiv = styled.div<TableCellStyleProps>`
  padding: 0 12px;
  overflow: hidden;
  white-space: nowrap;
  display: flex;
  flex-flow: row;
  height: 100%;
  align-items: center;

  ${(p) => getCellBackgroundColor(p.$isHovering, p.$isSelected)}
  ${(p) => getWidthSettingsStyles(p.$settings?.cellWidth, p.$index)}
  ${(p) =>
    getCellIndexStyles(
      p.$index,
      p.$isSelected,
      p.$shouldRemoveTopBorderRadius,
      p.$shouldRemoveBottomBorderRadius,
    )}
`;

interface StyledAccountAvatarDivProps {
  $avatarSize: number;
}
const StyledAccountAvatarDiv = styled.div<StyledAccountAvatarDivProps>`
  flex: 0 0 ${(p) => p.$avatarSize}px;
  margin-right: 12px;
`;

const StyledAccountDetailsContainerDiv = styled.div`
  flex: 1;
  display: flex;
  flex-flow: column;
  overflow: hidden;
`;

interface StyledAccountNameDivProps {
  $isSelected: boolean;
}
const StyledAccountNameDiv = styled.div<StyledAccountNameDivProps>`
  display: flex;
  align-items: center;
  flex: 1;
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.bold};

  ${ellipsis()};
  ${(p) => getCellTextColor(p.$isSelected)}
`;

interface StyledAccountDomainsDivProps {
  $isSelected: boolean;
}
const StyledAccountDomainsDiv = styled.div<StyledAccountDomainsDivProps>`
  display: block;
  flex: 1;
  font-size: ${fontSizes.verySmall};
  margin-top: 4px;
  ${ellipsis()};

  ${(p) => getCellTextColor(p.$isSelected, p.theme.greys.shade70)}
`;

/*
 * Component.
 */

export const AccountCell = (props: TableCellPropsWithAvatar<AccountModel>): ReactElement => {
  const {
    isSelected,
    isHovering,
    isLoading,
    size,
    settings,
    index,
    avatarSize,
    shouldShowStickyGradient,
    shouldRemoveTopBorderRadius,
    shouldRemoveBottomBorderRadius,
  } = props;

  const avatarSizePx = getTableCellAvatarSize(size, avatarSize);
  const avatarIconSize = getTableCellAvatarIconSize(size, avatarSize);

  return (
    <TableCell<AccountModel>
      isSelected={isSelected}
      isHovering={isHovering}
      isLoading={isLoading}
      size={size}
      settings={settings}
      index={index}
      value={props.value}
      shouldShowStickyGradient={shouldShowStickyGradient}
      shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
      shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
      renderLoading={() => (
        <StyledAccountCellContainerDiv
          $settings={settings}
          $isHovering={false}
          $isSelected={Boolean(isSelected)}
          $index={index}
          $shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
          $shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
        >
          <StyledAccountAvatarDiv $avatarSize={avatarSizePx}>
            <Skeleton
              height={`${avatarSizePx}px`}
              width={`${avatarSizePx}px`}
              borderRadius="50%"
              scheme={settings?.loading?.style}
            />
          </StyledAccountAvatarDiv>
          <StyledAccountDetailsContainerDiv>
            <StyledAccountNameDiv $isSelected={false}>
              <Skeleton height="16px" width="140px" borderRadius="4px" scheme={settings?.loading?.style} />
            </StyledAccountNameDiv>
            <StyledAccountDomainsDiv $isSelected={false}>
              <Skeleton
                height="14px"
                width="214px"
                borderRadius="4px"
                scheme={SkeletonColorSchemesEnum.GREY10}
              />
            </StyledAccountDomainsDiv>
          </StyledAccountDetailsContainerDiv>
        </StyledAccountCellContainerDiv>
      )}
      renderCell={(value) => (
        <StyledAccountCellContainerDiv
          $settings={settings}
          $isHovering={Boolean(isHovering)}
          $index={index}
          $isSelected={Boolean(isSelected)}
          $shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
          $shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
        >
          <StyledAccountAvatarDiv $avatarSize={avatarSizePx}>
            <AccountAvatar account={value} size={avatarSizePx} avatarIconSize={avatarIconSize} />
          </StyledAccountAvatarDiv>
          <StyledAccountDetailsContainerDiv>
            <StyledAccountNameDiv $isSelected={Boolean(isSelected)}>{value.name}</StyledAccountNameDiv>
            {maybeRenderDomains(value.domains, isSelected)}
          </StyledAccountDetailsContainerDiv>
        </StyledAccountCellContainerDiv>
      )}
    />
  );
};

/*
 * Helpers.
 */

const maybeRenderDomains = (domains: ReadonlyArray<string>, isSelected?: boolean) => {
  if (domains.length === 0) {
    return null;
  }
  return (
    <StyledAccountDomainsDiv $isSelected={Boolean(isSelected)}>{domains.join(', ')}</StyledAccountDomainsDiv>
  );
};
